import type { components } from '@connected/api-client/api.generated';

import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Checkbox } from '@connected/ui/components/checkbox';
import { Section } from '@connected/ui/features/layouts';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import type { DeviceActions } from '@/components/features/residual-current-device';

import { ResidualCurrentDeviceTable } from '@/components/features/residual-current-device';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useCondition } from '@/config/conditions';
import { queryClient } from '@/lib/query-client';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/residual-current-devices/',
)({
    component: Page,
    validateSearch: z.object({
        page: z.coerce.number().optional().default(1),
    }),
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(
            elektraAppQueryOptions.project.distributionBoardDeviceListOptions({
                electricalProjectVersionId: params.projectVersionId,
            }),
        );
        context.queryClient.ensureQueryData(
            elektraAppQueryOptions.project.inspection.distributionBoard.options.residualCurrentDeviceOptionsListOptions(),
        );
    },
});

function Page() {
    const { t: c } = useTranslation('common');
    const { t } = useTranslation('re-inspections');

    const params = Route.useParams();
    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    const enableExtendedRiso = useCondition('canAccessExtendedRiso');

    const { data: distributionBoardData } = useQuery(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId }));

    // Ref to access device actions from the table component
    const actionsRef = useRef<DeviceActions | null>(null);

    const patcher = usePatcher('/api/v2/electrical/projects/distributionboards/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables!.body!);

            const distributionBoardQueryOptions = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId });

            await queryClient.cancelQueries(distributionBoardQueryOptions);

            const previousDistributionBoardData = queryClient.getQueryData(distributionBoardQueryOptions.queryKey) as components['schemas']['EditElectricalProjectDistributionBoardQueryResult'];
            const newDistributionBoardData: components['schemas']['EditElectricalProjectDistributionBoardQueryResult'] = {
                ...previousDistributionBoardData,
                DistributionBoard: {
                    ...previousDistributionBoardData.DistributionBoard,
                    ...body,
                },
            };

            queryClient.setQueryData(distributionBoardQueryOptions.queryKey, newDistributionBoardData);

            return {
                previousData: previousDistributionBoardData,
                queryKey: distributionBoardQueryOptions.queryKey,
            };
        },
        onSettled: async (_data, _variables, context: any) => {
            await queryClient.invalidateQueries({ queryKey: context.queryKey });
        },
        scope: {
            id: params.projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectDistributionBoardId: params.distributionboardId,
        },
    });

    async function handleNewDevice() {
        await actionsRef.current?.create();
    }

    return (
        <Section>
            <Section.Header className="flex-row justify-between items-center">
                <Section.Header.Title>{t('distribution-box.layout.navigation.residual-current-devices.title')}</Section.Header.Title>
                <Section.Header.Actions>
                    <label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
                        <Checkbox
                            checked={distributionBoardData?.DistributionBoard?.ReportResidualCurrentDevices !== false}
                            onCheckedChange={(checked) => {
                                patcher.patchField('ReportResidualCurrentDevices')({ value: checked });
                            }}
                        />
                        {c('report.include-in-report')}
                    </label>
                    <Section.Header.Action
                        variant="default"
                        icon={<Plus size={20} />}
                        onClick={handleNewDevice}
                    />
                </Section.Header.Actions>
            </Section.Header>
            <Section.Content>
                <ResidualCurrentDeviceTable
                    electricalProjectVersionId={params.projectVersionId}
                    electricalProjectDistributionBoardId={params.distributionboardId}
                    enableExtendedRiso={enableExtendedRiso}
                    currentPage={search.page}
                    onPageChange={page => navigate({ search: prev => ({ ...prev, page }) })}
                    actionsRef={actionsRef}
                />
            </Section.Content>
            <Section.Footer>
                <FooterNavigation />
            </Section.Footer>
        </Section>
    );
}
